import type { JourneyNode } from '../../types/journey-nodes'
import { isBranchingNode, getBranchingNodeData } from './traversal'
import { getNodeOutStepId } from './traversal'

/**
 * Cache for branch scope computations within a single mutation cycle.
 * Create one at the start of a mutation helper; discard after the helper returns.
 *
 * MUST NOT be stored in module scope, component state, or reused across
 * separate mutation helper invocations. Each mutation cycle creates its own
 * instance. This prevents stale scope data from leaking across mutations
 * that modify the graph between cycles.
 */
export class ScopeCache {
    private cache = new Map<string, Set<string>>()

    /**
     * Get or compute scopeNodes for a branching node.
     */
    getScopeNodes(branchingNodeId: string, nodeMap: Map<string, JourneyNode>): Set<string> {
        const cached = this.cache.get(branchingNodeId)
        if (cached) return cached

        const node = nodeMap.get(branchingNodeId)
        if (!node) return new Set()

        const result = computeScopeNodes(node, nodeMap)
        this.cache.set(branchingNodeId, result)
        return result
    }
}

/**
 * Compute scopeNodes(B): the union of all nodes reachable from each of B's
 * branch out_step_id values, up to but excluding B's join_step_id.
 *
 * Traversal follows out_step_id chains and recurses into nested branching
 * nodes' branches (but does NOT cross the nested node's join_step_id boundary
 * — those nodes are still within the outer scope).
 */
export function computeScopeNodes(
    branchingNode: JourneyNode,
    nodeMap: Map<string, JourneyNode>,
): Set<string> {
    const data = getBranchingNodeData(branchingNode)
    if (!data) return new Set()

    const stopId = data.join_step_id != null ? String(data.join_step_id) : null
    const scope = new Set<string>()

    for (const branch of data.branches) {
        if (branch.out_step_id == null) continue
        walkScope(String(branch.out_step_id), stopId, nodeMap, scope)
    }

    return scope
}

/**
 * Walk from startId collecting nodes into `scope`, stopping at stopId.
 * When encountering a nested branching node, recurse into its branches
 * and then continue from its join_step_id.
 *
 * CRITICAL guards to prevent scope leakage through nested joins:
 * - Check currentId === stopId BEFORE any recursion or collection
 * - When recursing into nested branches: skip if branchStartId === stopId
 * - After processing nested branches: break if nestedJoinId === stopId
 */
function walkScope(
    startId: string,
    stopId: string | null,
    nodeMap: Map<string, JourneyNode>,
    scope: Set<string>,
): void {
    let currentId: string | null = startId

    while (currentId != null) {
        // Check stop boundary BEFORE any recursion or collection
        if (currentId === stopId) break
        if (scope.has(currentId)) break

        const node = nodeMap.get(currentId)
        if (!node) break

        scope.add(currentId)

        if (isBranchingNode(node)) {
            const nested = getBranchingNodeData(node)
            if (nested) {
                // Recurse into nested branches
                for (const branch of nested.branches) {
                    if (branch.out_step_id == null) continue
                    const branchStartId = String(branch.out_step_id)
                    // Guard: skip if branch out_step_id is the parent stop boundary
                    if (branchStartId === stopId) continue
                    walkScope(branchStartId, stopId, nodeMap, scope)
                }
                // Continue from nested join — but break if it's the stop boundary
                const nestedJoinId = nested.join_step_id != null ? String(nested.join_step_id) : null
                if (nestedJoinId === stopId) break
                currentId = nestedJoinId
                continue
            }
        }

        // Linear node: follow out_step_id
        currentId = getNodeOutStepId(node)
    }
}

/**
 * Find all branching nodes whose scope contains the given node.
 * Used to determine if an edge is inside any branch scope.
 */
export function enclosingBranchingNodes(
    nodeId: string,
    allNodes: JourneyNode[],
    scopeCache: ScopeCache,
): JourneyNode[] {
    const nodeMap = new Map(allNodes.map((n) => [n.id, n]))
    const enclosing: JourneyNode[] = []

    for (const node of allNodes) {
        if (!isBranchingNode(node)) continue
        const scope = scopeCache.getScopeNodes(node.id, nodeMap)
        if (scope.has(nodeId)) {
            enclosing.push(node)
        }
    }

    return enclosing
}
